import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { cn } from '@restworld/utils-common';
import { Alert } from '@restworld/ui-components';
import { TextInput } from '@restworld/ui-ds-base';

import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import { useEmailSmartValidation, validateEmailFormat } from './use-email-smart-validation';

interface EmailFieldWithSuggestionsProps {
  label: string;
  required: string;
}

const EmailFieldWithSuggestions: React.FC<EmailFieldWithSuggestionsProps> = ({ label, required }) => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');

  const { suggestion, mxWarning, isCheckingMx, applySuggestion, dismissSuggestion, dismissMxWarning, runChecks } =
    useEmailSmartValidation(setValue, trigger);

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
      <h5 className="justify-center text-neutral-700 text-base font-medium">{label}</h5>

      <Controller
        control={control}
        name="email"
        rules={{
          required,
          validate: (value) => {
            const errorKey = validateEmailFormat(value);
            return errorKey ? t(errorKey) : true;
          },
        }}
        render={({ field }) => (
          <div
            className="w-full"
            onBlur={() => {
              field.onBlur();
              runChecks(field.value || '');
            }}
          >
            <TextInput
              {...field}
              value={field.value || ''}
              placeholder=""
              id="email"
              type="email"
              isFullwidth
              supportText={errors.email?.message}
              isError={!!errors.email}
            />
          </div>
        )}
      />

      {/* Mailcheck typo suggestion */}
      {suggestion && (
        <Alert
          variant="info"
          className="w-full"
          textClassName="w-full"
          text={
            <span className="flex items-center justify-between gap-2 w-full">
              <span>{t('email_did_you_mean', { suggestion: suggestion.full })}</span>
              <span className="flex items-center gap-3 shrink-0">
                <button type="button" onClick={applySuggestion} className="font-semibold underline cursor-pointer">
                  {t('email_fix_suggestion')}
                </button>
                <button type="button" onClick={dismissSuggestion} className="opacity-50 cursor-pointer">
                  ✕
                </button>
              </span>
            </span>
          }
        />
      )}

      {/* MX record warning */}
      {mxWarning && !suggestion && (
        <Alert
          variant="warning"
          className="w-full"
          textClassName="w-full"
          text={
            <span className="flex items-center justify-between gap-2 w-full">
              <span>{t('email_mx_warning', { domain: mxWarning })}</span>
              <button type="button" onClick={dismissMxWarning} className="opacity-50 cursor-pointer shrink-0">
                ✕
              </button>
            </span>
          }
        />
      )}

      {/* MX check loading indicator */}
      {isCheckingMx && (
        <p className="text-neutral-300 text-sm">{t('email_checking_domain') || 'Verifica dominio in corso...'}</p>
      )}

      <p className="text-neutral-300">
        *{t('email_notification_hint') || 'Ti avviseremo qui quando ricevi candidature'}
      </p>
    </div>
  );
};

export default EmailFieldWithSuggestions;
